import { useState } from 'react';
import { app, describeError, go } from '../state/app';
import { ApiError } from '../api/client';
import { previewCommission, selectCandidate } from '../api/endpoints';
import { once } from '../lib/once';
import { commissionPreviewKey } from '../lib/keys';
import { mediaUrl } from '../lib/media';
import { suggestName } from '../lib/names';

/**
 * Screen 3. Naming generates nothing, so nothing here is worth speculating
 * with money — but this screen is where the money already being spent gets to
 * run: the free peek starts on the candidate screen's tap, so the seconds the
 * parent spends here are the seconds the sketch is being drawn in. That is the
 * whole reason the box below starts empty rather than pre-filled.
 */
export function NameIt() {
  const { entry, characterId, character, chosenIndex, characterName } = app.use();
  // TWO ARRIVALS, TWO BOXES, and they must not be confused.
  //
  //   Fresh — the child has just tapped a tile and nothing is named yet.
  //   `characterName` is '', so the box is empty and autofocused: there is
  //   something to type, and typing it is what covers the peek.
  //
  //   Returning — the summary's back button, after the name was already locked
  //   on the server. An empty box there implied the naming had not happened
  //   when it had, so it is seeded. characters.py will refuse to change it
  //   (409, handled below), and showing a name we cannot alter is honest.
  //
  // Read at mount only. The submit below writes characterName, and re-seeding
  // from it mid-edit would overwrite what the parent was in the middle of.
  const [name, setName] = useState(characterName);
  const fresh = characterName === '';
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Offered, not imposed: the field shows it as a placeholder and submitting
  // empty accepts it, so the flow still never blocks on typing.
  const suggestion = suggestName(characterId ?? entry?.starterSlug ?? entry?.canonical);
  const candidate = character?.candidates?.[chosenIndex ?? 0];
  const hero = candidate?.url ?? mediaUrl(candidate?.key ?? candidate?.storageKey) ?? entry?.portraitUrl;

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!characterId) {
      setError('no character draft — go back and pick again');
      return;
    }
    const chosen = name.trim() || suggestion;
    setBusy(true);
    setError(null);
    try {
      let named = chosen;
      try {
        await selectCandidate(characterId, chosenIndex ?? 0, chosen);
      } catch (err) {
        // 409 IS NOT A FAILURE HERE. characters.py only accepts a selection
        // while the row is `awaiting_selection`, so pressing this button a
        // second time — which is what the summary's back button invites — got
        // the server's own "character is not awaiting selection" painted on
        // this screen, with no way past it: going back and picking again lands
        // here with the same 409, and the only escape was "start over", which
        // throws the character away and re-runs the whole wizard.
        //
        // The step it is refusing has already succeeded, so carry on. The name
        // that counts is the one already stored on the row — plan.py reads it
        // from there — so a retype on the second pass does not take, and this
        // uses the locked name rather than pretending otherwise.
        if (!(err instanceof ApiError) || err.status !== 409) throw err;
        named = app.get().characterName || chosen;
      }
      app.set({ characterName: named });
      // Free dry run, memoized so re-entering the summary never re-plans.
      // Usually a no-op now: the candidate screen's peek pre-warm needs the
      // premise to build its key, so it goes through this same memo first.
      // Kept because it is the guarantee, not the optimisation — if the
      // pre-warm was switched off or never ran, the summary must still find a
      // plan waiting rather than starting one on arrival.
      void once(commissionPreviewKey(characterId), () => previewCommission(characterId)).catch(
        () => undefined,
      );
      go('summary');
    } catch (err) {
      setError(describeError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="pane-img">
        {hero ? <img src={hero} alt="" /> : <div className="placeholder">picked ✨</div>}
      </div>
      <div className="pane-q">
        <button className="backbtn" onClick={() => go('candidates')}>
          ‹ back
        </button>
        <h1>Picked!</h1>
        <p className="sub">Give them a name, and the first film plans itself.</p>

        {error && <div className="err">{error}</div>}

        <form onSubmit={submit}>
          <input
            className="namein"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={suggestion}
            aria-label="character name"
            /* Only on a fresh arrival. Focusing a box that already holds a
               locked name invites an edit the server will refuse. */
            autoFocus={fresh}
          />
          <button className="big" type="submit" disabled={busy}>
            {busy ? 'saving…' : "That's the name"}
          </button>
        </form>
      </div>
    </>
  );
}
